import type { FC } from "react";
import { FONT_SIZE, ROLE_BADGE } from "../../../theme/tokens";
import type { NurseFullRole } from "../../../utils/models";
import ColorfulRole from "../../UIToolkit/ColorfulRole/ColorfulRole";
import { ROW_BADGE_GEOMETRY_SX, ROW_LEAD_WIDTH_PX } from "./gapsRowLayout";

/** Where the badge sits: a list row (the home's roster, a sheet's list, an applicant line) or a sheet's heading. */
export type GapsRoleBadgeSize = "row" | "heading";

type Props = {
	role: NurseFullRole | string;
	size: GapsRoleBadgeSize;
};

/**
 * `minWidth` rather than `width` at the row scale: the badge fills the rows' shared leading column, so every name starts
 * where "Missing nurse" does and the badges stack as one even column, while a longer role code grows its own row rather
 * than being clipped.
 */
const SIZE_SX = {
	row: { minWidth: ROW_LEAD_WIDTH_PX, ...ROW_BADGE_GEOMETRY_SX, fontWeight: 600, flex: "none" },
	heading: {
		minWidth: ROLE_BADGE.HEADING.MIN_WIDTH,
		height: ROLE_BADGE.HEADING.HEIGHT,
		px: 0.75,
		borderRadius: ROLE_BADGE.HEADING.RADIUS,
		fontSize: FONT_SIZE.LABEL_XS,
		fontWeight: 700,
		flex: "none",
	},
} as const satisfies Record<GapsRoleBadgeSize, object>;

/**
 * A person's role on the gaps-first view — the app's own role colours (`ColorfulRole`), the artifact's micro badge
 * (`.rb`) — at one of two scales. One component for every place a person is listed, so a person reads the same on a
 * roster row, in the call-in and add lists, on an Open Shifts applicant line and at the head of their own sheet.
 */
export const GapsRoleBadge: FC<Props> = ({ role, size }) => <ColorfulRole role={role} sx={SIZE_SX[size]} />;
